import { parseDate, CalendarDate } from '@internationalized/date';
import moment from 'moment';

export type CalendarQuarterDefault = Readonly<{
  quarter: number;
  year: number;
}>;

export type CalendarMonthDefault = Readonly<{
  month: string;
  year: number;
}>;

/** Props accepted by {@link CalendarRange} for initial selection (mutually exclusive). */
export type CalendarRangeDefaultProps = Readonly<{
  defaultDate?: CalendarDate;
  defaultQuarter?: CalendarQuarterDefault;
  defaultMonth?: CalendarMonthDefault;
}>;

export type CalendarDateFieldSource = Readonly<{
  /** ISO day (YYYY-MM-DD prefix). */
  isoDay?: string;
  month?: string;
  quarter?: string;
}>;

const ISO_DAY_PREFIX = /^\d{4}-\d{2}-\d{2}/;

/** Parse `Q2 2026` / `Q2, 2026` for calendar quarter defaults. */
export function parseQuarterLabelForCalendar(
  value?: string
): CalendarQuarterDefault | undefined {
  if (!value?.trim()) return undefined;
  const match = value
    .replace(/,/g, '')
    .trim()
    .match(/^Q([1-4])\s+(\d{4})$/i);
  if (!match) return undefined;
  return { quarter: parseInt(match[1], 10), year: parseInt(match[2], 10) };
}

/**
 * Normalize month strings (`Oct 2026`, `October, 2025`, `2025-10`) to `{ month: 'Oct', year }`
 * using labels the shared calendar month picker can parse.
 */
export function normalizeMonthLabelForCalendar(
  monthStr: string
): CalendarMonthDefault | undefined {
  const cleaned = monthStr.replace(/,/g, '').trim();
  if (!cleaned) return undefined;

  const parsed = moment(
    cleaned,
    ['MMM YYYY', 'MMMM YYYY', 'YYYY-MM', 'MM-YYYY', 'MM/YYYY'],
    true
  );
  if (parsed.isValid()) {
    const month = parsed.format('MMM');
    const year = parsed.year();
    if (isBrowserParseableMonth(month, year)) {
      return { month, year };
    }
  }

  const match = cleaned.match(/^([A-Za-z]+)\s+(\d{4})$/);
  if (match) {
    const month = match[1];
    const year = parseInt(match[2], 10);
    if (isBrowserParseableMonth(month, year)) {
      return { month, year };
    }
  }

  return undefined;
}

function isBrowserParseableMonth(month: string, year: number): boolean {
  return Number.isFinite(new Date(`${month} 1, ${year}`).getMonth());
}

function parseIsoDay(value?: string): CalendarDate | undefined {
  if (!value || !ISO_DAY_PREFIX.test(value)) return undefined;
  try {
    return parseDate(value.substring(0, 10));
  } catch {
    return undefined;
  }
}

function isQuarterLike(value: string): boolean {
  return /^Q/i.test(value.trim());
}

function isIsoDayLike(value: string): boolean {
  return ISO_DAY_PREFIX.test(value);
}

/**
 * Resolve {@link CalendarRange} default props from field values + optional in-progress edit value.
 * Returns at most one of day / month / quarter (same contract as RSD slideout date fields).
 */
export function resolveCalendarRangeDefaults(
  fields: CalendarDateFieldSource,
  activeValue = ''
): CalendarRangeDefaultProps {
  const quarter =
    parseQuarterLabelForCalendar(activeValue) ||
    parseQuarterLabelForCalendar(fields.quarter);
  if (quarter) {
    return { defaultQuarter: quarter };
  }

  const monthSource =
    activeValue && !isQuarterLike(activeValue) && !isIsoDayLike(activeValue)
      ? activeValue
      : fields.month;
  const month = monthSource
    ? normalizeMonthLabelForCalendar(monthSource)
    : undefined;
  if (month) {
    return { defaultMonth: month };
  }

  const defaultDate =
    parseIsoDay(fields.isoDay) ||
    parseIsoDay(isIsoDayLike(activeValue) ? activeValue : undefined);
  if (defaultDate) {
    return { defaultDate };
  }

  return {};
}
